// src/interaction/modes/Cursor.ts
// UPDATED: Auto-select all group members ONLY when group is locked
// FIXED: Include connector waypoints when dragging grouped items
import { produce } from 'immer';
import { ModeActions, ModeActionsAction, ItemReference, ConnectorAnchor, Coords } from 'src/types';
import { generateId, getItemsAtTile, getItemByIdOrThrow, hasMovedTile, getAnchorTile } from 'src/utils';
import { groupManager } from '@/features/groups/GroupManager';
import { GroupableElement } from '@/features/groups/groupTypes';

let lastCycleKey: string | null = null;
let lastCycleIndex = 0;
let lastClickedItemKey: string | null = null;
let lastClickedAt = 0;

const mousedown: ModeActionsAction = ({
  uiState,
  scene,
  isRendererInteraction,
  mouseEvent
}) => {
  if (uiState.mode.type !== 'CURSOR' || !isRendererInteraction) return;

  const candidates = getItemsAtTile({
    tile: uiState.mouse.position.tile,
    scene
  });
  const tile = uiState.mouse.position.tile;
  const cycleKey = getSelectionCycleKey(tile, candidates);
  // Once the user has clicked through a stack, a subsequent drag should act
  // on that chosen object instead of jumping back to the topmost icon.
  const directItem = getDirectSceneItem(mouseEvent.target);
  // Connector handles are precise drag targets. Always prefer the handle over
  // the tile cycle; otherwise an attached endpoint can be mistaken for the
  // connector stroke and dragging creates/moves a waypoint instead.
  const itemAtTile = directItem?.type === 'CONNECTOR_ANCHOR'
    ? directItem
    : cycleKey === lastCycleKey
    ? candidates[lastCycleIndex] ?? directItem ?? candidates[0]
    : directItem ?? candidates[0];

  if (itemAtTile) {
    uiState.actions.setMode(
      produce(uiState.mode, (draft) => {
        draft.mousedownItem = itemAtTile;
      })
    );
  } else {
    lastCycleKey = null;
    lastCycleIndex = 0;
    uiState.actions.setSelectionCycle(null);
    uiState.actions.setMode(
      produce(uiState.mode, (draft) => {
        draft.mousedownItem = null;
      })
    );
    uiState.actions.setItemControls(null);
  }
};

// Helper to convert ItemReference to GroupableElement
const toGroupableElement = (itemRef: ItemReference): GroupableElement => ({
  type: itemRef.type as any,
  id: itemRef.id
});

// Helper to convert GroupableElement to ItemReference
const toItemReference = (element: GroupableElement): ItemReference => ({
  type: element.type,
  id: element.id
});

const getDirectSceneItem = (target: EventTarget | null): ItemReference | null => {
  if (!(target instanceof Element)) return null;
  const sceneElement = target.closest<HTMLElement>(
    '[data-scene-item-id][data-scene-item-type]'
  );
  const id = sceneElement?.dataset.sceneItemId;
  const type = sceneElement?.dataset.sceneItemType as ItemReference['type'] | undefined;
  return id && type ? { id, type } : null;
};

const getSelectionCycleKey = (tile: Coords, candidates: ItemReference[]) => {
  const candidateSignature = candidates
    .map((candidate) => `${candidate.type}:${candidate.id}`)
    .join('|');
  return `${tile.x},${tile.y}:${candidateSignature}`;
};

const distanceToSegmentSquared = (point: Coords, from: Coords, to: Coords) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) {
    return (point.x - from.x) ** 2 + (point.y - from.y) ** 2;
  }
  const t = Math.max(0, Math.min(1,
    ((point.x - from.x) * dx + (point.y - from.y) * dy) / (dx * dx + dy * dy)
  ));
  const nearest = { x: from.x + t * dx, y: from.y + t * dy };
  return (point.x - nearest.x) ** 2 + (point.y - nearest.y) ** 2;
};

const getWaypointInsertionIndex = (
  anchors: ConnectorAnchor[],
  point: Coords,
  view: Parameters<typeof getAnchorTile>[1]
) => {
  let segmentIndex = 0;
  let bestDistance = Infinity;
  for (let index = 0; index < anchors.length - 1; index += 1) {
    const distance = distanceToSegmentSquared(
      point,
      getAnchorTile(anchors[index], view),
      getAnchorTile(anchors[index + 1], view)
    );
    if (distance < bestDistance) {
      bestDistance = distance;
      segmentIndex = index;
    }
  }
  return segmentIndex + 1;
};

/**
 * Find all waypoint anchors from connectors that are attached to the given items
 * When dragging grouped items, we need to also drag the waypoints of any connectors
 * that connect to those items, otherwise the connectors will break.
 * 
 * @param itemIds - Array of item IDs that are being dragged
 * @param connectors - All connectors in the scene
 * @returns Array of ItemReferences for all waypoints that should move with the items
 */
export const getConnectedWaypoints = (): ItemReference[] => {
  const waypoints: ItemReference[] = [];
  if (waypoints.length > 0) {
    console.log(`🔗 Found ${waypoints.length} connector waypoints to drag with group`);
  }
  return waypoints;
};

export const Cursor: ModeActions = {
  entry: (state) => {
    const { uiState } = state;
    if (uiState.mode.type !== 'CURSOR') return;
    if (uiState.mode.mousedownItem) {
      mousedown(state);
    }
  },

  mousemove: ({ scene, uiState }) => {
    if (uiState.mode.type !== 'CURSOR' || !hasMovedTile(uiState.mouse)) return;

    const item = uiState.mode.mousedownItem;
    if (!item) return;

    // Collect items to drag
    let itemsToDrag: ItemReference[] = [];

    // Reuse a multi-selection only when the pointer went down on one of its
    // members. A previous selection must not own the next element's drag.
    const isMousedownItemSelected = uiState.itemControls?.type === 'MULTI_SELECT'
      ? uiState.itemControls.items.some(
        (selected) => selected.type === item.type && selected.id === item.id
      )
      : Boolean(
        uiState.itemControls &&
        uiState.itemControls.type !== 'ADD_ITEM' &&
        uiState.itemControls.type === item.type &&
        uiState.itemControls.id === item.id
      );

    if (uiState.itemControls?.type === 'MULTI_SELECT' && isMousedownItemSelected) {
      itemsToDrag = uiState.itemControls.items;
    } else {
      // Transfer selection ownership immediately so the previous item/group
      // loses both its visual feedback and its interaction binding.
      if (!isMousedownItemSelected) {
        if (
          item.type === 'ITEM' ||
          item.type === 'RECTANGLE' ||
          item.type === 'TEXTBOX' ||
          item.type === 'CONNECTOR'
        ) {
          uiState.actions.setItemControls(item);
        } else if (item.type === 'CONNECTOR_ANCHOR') {
          const parent = scene.connectors.find((connector) =>
            connector.anchors.some((anchor) => anchor.id === item.id)
          );
          uiState.actions.setItemControls(parent
            ? { type: 'CONNECTOR', id: parent.id }
            : null);
        } else {
          uiState.actions.setItemControls(null);
        }
      }

      // ✅ Check if the single item being dragged is in a LOCKED group
      const clickedElement = item.type === 'CONNECTOR_ANCHOR'
        ? null
        : toGroupableElement(item);
      const group = clickedElement
        ? groupManager.getGroupForElement(clickedElement)
        : undefined;

      if (group && group.locked && groupManager.getGroup(group.id)) {
        // Item is in a LOCKED group - drag ALL group members together
        const groupElements = groupManager.getGroupElements(group.id);
        itemsToDrag = groupElements.map(toItemReference);

        // ✅ Update itemControls to MULTI_SELECT so highlights appear
        if (itemsToDrag.length > 1) {
          uiState.actions.setItemControls({
            type: 'MULTI_SELECT',
            items: itemsToDrag
          });
        }
      } else {
        // ✅ NOT in a group - check if it's a connector line that needs waypoint
        if (item.type === 'CONNECTOR' && uiState.mouse.mousedown) {
          try {
            const connector = getItemByIdOrThrow(scene.connectors, item.id).value;

            // Create waypoint at drag start position
            const newAnchor: ConnectorAnchor = {
              id: generateId(),
              ref: { tile: uiState.mouse.mousedown.tile }
            };

            // Insert into the segment the user actually grabbed. Always
            // inserting after the first endpoint corrupts anchor order after
            // repeated edits and creates large self-crossing paths.
            const insertionIndex = getWaypointInsertionIndex(
              connector.anchors,
              uiState.mouse.mousedown.tile,
              scene.currentView
            );
            const updatedAnchors = [
              ...connector.anchors.slice(0, insertionIndex),
              newAnchor,
              ...connector.anchors.slice(insertionIndex)
            ];

            // Update connector with new waypoint (DON'T simplify yet!)
            scene.updateConnector(item.id, { anchors: updatedAnchors });

            // The connector remains the owner of the interaction while its
            // newly created waypoint is dragged. Without this, DRAG_ITEMS has
            // an anchor payload but the connector loses selected rendering.
            uiState.actions.setItemControls({
              type: 'CONNECTOR',
              id: item.id
            });

            // Switch to dragging the new waypoint
            uiState.actions.setMode({
              type: 'DRAG_ITEMS',
              showCursor: true,
              items: [{ type: 'CONNECTOR_ANCHOR', id: newAnchor.id }],
              isInitialMovement: true
            });

            return; // Exit early after creating waypoint
          } catch (error) {
            console.error('❌ Failed to create waypoint:', error);
          }
        }

        // Single item drag (not grouped, not connector needing waypoint)
        itemsToDrag = [item];
      }
    }

    // ✅ FIX: When dragging items (especially in groups), also drag connected waypoints
    // Get all ITEM type items that are being dragged
    // Item-referenced endpoints already follow the moved items. Tile-based
    // waypoints remain fixed instead of being pulled along by the endpoint.

    // ✅ Only drag draggable item types
    // IMPORTANT: CONNECTOR is NOT included - connectors follow their anchored items
    // Only CONNECTOR_ANCHOR is draggable (to reshape connectors)
    const draggableItems = itemsToDrag.filter(
      (i) => i.type === 'ITEM' ||
        i.type === 'RECTANGLE' ||
        i.type === 'TEXTBOX' ||
        i.type === 'CONNECTOR_ANCHOR'  // Only anchors/waypoints are draggable
    );

    if (draggableItems.length > 0) {
      uiState.actions.setMode({
        type: 'DRAG_ITEMS',
        showCursor: true,
        items: draggableItems,
        isInitialMovement: true
      });
    }
  },

  mousedown,

  mouseup: ({ uiState, scene, isRendererInteraction, mouseEvent }) => {
    if (uiState.mode.type !== 'CURSOR' || !isRendererInteraction) return;

    const mousedownItem = uiState.mode.mousedownItem;
    const isCtrlHeld = mouseEvent.ctrlKey || mouseEvent.metaKey;

    // Only process clicks (not drags)
    if (mousedownItem && !hasMovedTile(uiState.mouse)) {
      let item: ItemReference = mousedownItem;
      if (!isCtrlHeld) {
        const directItem = getDirectSceneItem(mouseEvent.target);
        const tile = uiState.mouse.position.tile;
        const candidates = getItemsAtTile({ tile, scene });
        const cycleKey = getSelectionCycleKey(tile, candidates);
        const directIndex = directItem
          ? candidates.findIndex((candidate) =>
            candidate.type === directItem.type && candidate.id === directItem.id
          )
          : -1;
        const nextIndex = candidates.length === 0
          ? 0
          : cycleKey === lastCycleKey
            ? (lastCycleIndex + 1) % candidates.length
            : Math.max(0, directIndex);

        lastCycleKey = candidates.length > 0 ? cycleKey : null;
        lastCycleIndex = nextIndex;
        item = candidates[nextIndex] ?? directItem ?? item;
        uiState.actions.setSelectionCycle(candidates.length > 0 ? {
          tile,
          item,
          index: nextIndex,
          total: candidates.length
        } : null);
      }

      // A waypoint is an editing handle owned by its connector. Canonicalize
      // click selection to that parent; DRAG_ITEMS still carries the exact
      // anchor id while the point is actively being moved.
      if (item.type === 'CONNECTOR_ANCHOR') {
        const parent = scene.connectors.find((connector) =>
          connector.anchors.some((anchor) => anchor.id === item.id)
        );
        if (parent) item = { type: 'CONNECTOR', id: parent.id };
      }

      const clickedItemKey = `${item.type}:${item.id}`;
      const clickedAt = Date.now();
      const isDoubleClick = !isCtrlHeld &&
        clickedItemKey === lastClickedItemKey &&
        clickedAt - lastClickedAt <= 500;
      lastClickedItemKey = clickedItemKey;
      lastClickedAt = clickedAt;

      if (isCtrlHeld) {
        // Ctrl+Click: Toggle multi-selection
        if (uiState.itemControls?.type === 'MULTI_SELECT') {
          const currentItems = uiState.itemControls.items;
          const itemIndex = currentItems.findIndex(
            (i: ItemReference) => i.type === item.type && i.id === item.id
          );

          if (itemIndex >= 0) {
            // Remove from selection
            const newItems = currentItems.filter((_: any, i: number) => i !== itemIndex);
            if (newItems.length === 0) {
              uiState.actions.setItemControls(null);
            } else if (newItems.length === 1) {
              uiState.actions.setItemControls(newItems[0]);
            } else {
              uiState.actions.setItemControls({
                type: 'MULTI_SELECT',
                items: newItems
              });
            }
          } else {
            // Add to selection
            uiState.actions.setItemControls({
              type: 'MULTI_SELECT',
              items: [...currentItems, item]
            });
          }
        } else if (uiState.itemControls) {
          // Start multi-selection
          uiState.actions.setItemControls({
            type: 'MULTI_SELECT',
            items: [uiState.itemControls as ItemReference, item]
          });
        } else {
          // First item in selection
          if (item.type === 'ITEM') {
            uiState.actions.setItemControls({ type: 'ITEM', id: item.id });
          } else if (item.type === 'RECTANGLE') {
            uiState.actions.setItemControls({ type: 'RECTANGLE', id: item.id });
          } else if (item.type === 'CONNECTOR') {
            uiState.actions.setItemControls({ type: 'CONNECTOR', id: item.id });
          } else if (item.type === 'TEXTBOX') {
            uiState.actions.setItemControls({ type: 'TEXTBOX', id: item.id });
          }
        }
      } else {
        // Normal click: Single selection
        // ✅ Check if clicked item is in a LOCKED group
        const clickedElement = toGroupableElement(item);
        const group = groupManager.getGroupForElement(clickedElement);

        if (
          item.type !== 'CONNECTOR' &&
          group &&
          group.locked &&
          groupManager.getGroup(group.id)
        ) {
          // Item is in a LOCKED group - select ALL group members
          console.log('🔒 Selecting locked group, including all members');
          const groupElements = groupManager.getGroupElements(group.id);
          const groupItems = groupElements.map(toItemReference);

          if (groupItems.length > 1) {
            uiState.actions.setItemControls({
              type: 'MULTI_SELECT',
              items: groupItems
            });
          } else if (groupItems.length === 1) {
            // Single item in group - just select it normally
          //  const singleItem = groupItems[0];
            if (item.type === 'ITEM') {
              uiState.actions.setItemControls({ type: 'ITEM', id: item.id });
            } else if (item.type === 'RECTANGLE') {
              uiState.actions.setItemControls({ type: 'RECTANGLE', id: item.id });
            } else if (item.type === 'TEXTBOX') {
              uiState.actions.setItemControls({ type: 'TEXTBOX', id: item.id });
            }
          }
        } else {
          // Not in a group - normal single selection
          if (item.type === 'ITEM') {
            uiState.actions.setItemControls({ type: 'ITEM', id: item.id });
          } else if (item.type === 'RECTANGLE') {
            uiState.actions.setItemControls({ type: 'RECTANGLE', id: item.id });
          } else if (item.type === 'CONNECTOR') {
            uiState.actions.setItemControls({ type: 'CONNECTOR', id: item.id });
          } else if (item.type === 'TEXTBOX') {
            uiState.actions.setItemControls({ type: 'TEXTBOX', id: item.id });
          } else if (item.type === 'CONNECTOR_ANCHOR') {
            uiState.actions.setItemControls({
              type: 'CONNECTOR_ANCHOR',
              id: item.id
            });
          }
        }
      }

      // Detect the second click in the canvas interaction layer. Selecting an
      // item can re-render its DOM between clicks, which makes the browser's
      // native dblclick event unreliable for icons.
      if (isDoubleClick) {
        if (item.type === 'ITEM') {
          uiState.actions.setEditingModelItemId(item.id);
        } else if (item.type === 'TEXTBOX') {
          uiState.actions.setEditingTextBoxId(item.id);
        }
      }
    } else if (!mousedownItem) {
      // Clicked empty space - clear selection
      lastClickedItemKey = null;
      lastClickedAt = 0;
      uiState.actions.setItemControls(null);
    }

    // Clear mousedownItem
    uiState.actions.setMode(
      produce(uiState.mode, (draft) => {
        draft.mousedownItem = null;
      })
    );
  }
};
